import { type EventStandardDefs } from '@solinkcorp/event-standard';
import { type IEvent } from 'clients/PlatformApi';
import get from 'lodash/get';
import * as EventUtil from 'utils/EventUtil';
import FeaturesUtil from 'utils/FeaturesUtil';
import { type FieldRendererField } from './types';

/**
 * Select and rank event-standard fields that should render for an event
 * (detail pane and compact card field lists).
 */
export function getFieldsToRender(
  eventStandard: EventStandardDefs,
  event: IEvent,
): FieldRendererField[] {
  const isAlarm = EventUtil.isAlarm(event);
  const isScheduledAIChat = EventUtil.isScheduledAIChatEvent(event);
  const isAgent = event.subtype === 'agent';

  const fields = Object.keys(eventStandard)
    .filter((f) => {
      const visibility = eventStandard[f].visible || false;
      const isVisible = FeaturesUtil.canViewEventField(visibility);

      return (
        ((get(event, f, undefined) !== undefined && f !== 'details.Receipt') ||
          get(event, f, undefined)) &&
        isVisible &&
        (!isAlarm ||
          (isAlarm &&
            [
              'videoLinkItems',
              'details.Vehicle count',
              'details.People count',
              'details.Vehicle detection confidence',
              'details.People detection confidence',
              'locationTags',
            ].includes(f))) &&
        /** only show video link items for scheduled spot checks */
        (!isScheduledAIChat || (isScheduledAIChat && f === 'videoLinkItems')) &&
        /** hide subtype field for agent events — it's redundant */
        !(isAgent && f === 'subtype')
      );
    })
    .sort((f1, f2) => {
      const f1Rank = eventStandard[f1].rank || 0;
      const f2Rank = eventStandard[f2].rank || 0;

      if (f1Rank === f2Rank) {
        const name1 = EventUtil.i18nEventFieldName(f1, false, eventStandard);
        const name2 = EventUtil.i18nEventFieldName(f2, false, eventStandard);
        if (name1 > name2) {
          return 1;
        }
        if (name1 < name2) {
          return -1;
        }
        return 0;
      }
      return f1Rank - f2Rank;
    })
    .map((name) => ({ name, ...eventStandard[name] }));

  // Append fields with specific renderers that aren't in the event standard
  const skillMatches = get(event, 'details.skillMatches');
  if (Array.isArray(skillMatches) && skillMatches.length > 0) {
    fields.push({
      name: 'details.skillMatches',
      type: 'string',
      visible: true,
    } as FieldRendererField);
  }

  return fields;
}
